"use client";

import Link from "next/link";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { LuCheckCircle } from "react-icons/lu";
import { FaInfoCircle } from "react-icons/fa";

import { dedupe } from "~/utils/various";
import { slugifyUser } from "~/utils/string";
import { SocialLinks } from "~/types/UserTypes";
import { useEventContextV2 } from "~/app/providers";
import { getNonEmptySocialLinks, hasAnySocialLink } from "~/utils/user-helpers";

import {
    Card,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "~/components/v4/shadcn/Card";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/v4/shadcn/Tooltip";
import { If } from "~/components/v3/Conditional";
import { Button } from "~/components/v4/themed/Button";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { SocialLinksComponent } from "~/components/v3/users-components/SocialLinksComponent";
import { FollowUserButton } from "~/components/v4/themed/user/FollowUserButton";

import { getCurrentUserFollowing } from "../actions";
import Linkify from "linkify-react";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { cn } from "~/utils/fontsConfig";
import { ButtonLink } from "~/components/v3/themed/Button";

export const PostEvent = ({
    customization,
}: {
    customization?: CustomizableLayout;
}) => {
    const event = useEventContextV2();
    const [showAll, setShowAll] = useState(false);
    const { data: session } = useSession();

    const {
        data: following,
        refetch,
        isLoading,
    } = useQuery({
        queryKey: ["currentUserFollowing"],
        queryFn: () => getCurrentUserFollowing(),
    });

    const followingMap = new Map(following?.map((f) => [f.followedUserId, f]));
    const deduped = dedupe(
        [
            ...event.hosts.map((h) => ({
                user: { ...h.user, isHost: true, hasCheckedIn: true },
            })),
            ...event.attendees.map((a) => ({
                user: { ...a.user, isHost: false, hasCheckedIn: a.checkedIn },
            })),
        ].map((a) => a.user),
        "id",
    );

    const featuredAttendees = deduped
        .filter((u) => {
            if (showAll) return true;
            // at least 1 social link, bio, picture and name
            if (!u.name || u.name.length < 4) return false;
            if (!hasAnySocialLink(u)) return false;
            if (!u.bio || u.bio.length < 10) return false;
            if (!u.image) return false;
            return true;
        })
        .sort((u1, u2) => {
            // + hosts go first
            const hostDiff = Number(u2.isHost) - Number(u1.isHost);
            // + checkins go first
            const checkinDiff =
                Number(u2.hasCheckedIn) - Number(u1.hasCheckedIn);
            // + more social links
            const linksDiff =
                getNonEmptySocialLinks(u2).length -
                getNonEmptySocialLinks(u1).length;
            // + longer bio
            const rawBioDiff = (u2.bio?.length || 0) - (u1.bio?.length || 0);
            const bioDiff = Math.max(-100, Math.min(rawBioDiff, 100));
            return (
                1000 * hostDiff + 500 * checkinDiff + 10 * linksDiff + bioDiff
            );
        });

    return (
        <div>
            <div className="flex flex-col gap-2">
                <div className="flex items-center">
                    <span
                        className={cn(
                            "mb-2 grow text-xl",
                            customization?.descriptionClassName,
                        )}
                    >
                        Attendees ({deduped.length})
                    </span>
                    <Button
                        variant="primaryLink"
                        onClick={() => setShowAll(!showAll)}
                        className={cn(customization?.linkClassName)}
                    >
                        {showAll
                            ? "Show fewer attendees"
                            : "Show all attendees"}
                    </Button>
                </div>

                <p
                    className={cn(
                        "text-lg font-medium",
                        customization?.descriptionClassName,
                    )}
                >
                    Connect with your fellow attendees
                </p>

                <p
                    className={cn(
                        "break-words font-medium",
                        customization?.descriptionClassName,
                    )}
                >
                    <Linkify
                        options={{
                            className:
                                "text-v3-N300 hover:underline hover:text-v3-N100",
                            target: "_blank",
                            rel: "noopener noreferrer nofollow",
                        }}
                    >
                        Below, you'll find a list of attendees that RSVPed to
                        the event. Spend some time looking through the list to
                        visit socials and connect with other attendees from the
                        event
                    </Linkify>
                </p>

                <p
                    className={cn(
                        "break-words font-medium",
                        customization?.descriptionClassName,
                    )}
                >
                    <Linkify
                        options={{
                            className:
                                "text-v3-N300 hover:underline hover:text-v3-N100",
                            target: "_blank",
                            rel: "noopener noreferrer nofollow",
                        }}
                    >
                        The more full your profile is, the higher it gets ranked
                        on the post-event page. Don't be shy - share your
                        socials and bio!
                    </Linkify>
                </p>
            </div>

            <div
                className="mt-2 grid gap-4"
                style={{
                    gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
                }}
            >
                {featuredAttendees.map((attendee) => (
                    <Card
                        className={cn(
                            "relative flex flex-col",
                            customization?.secondaryCardClassName,
                        )}
                        key={attendee.id}
                    >
                        <CardHeader>
                            <CardTitle className="flex flex-col items-center justify-center gap-4">
                                <Link
                                    href={`/profile/${slugifyUser({
                                        name: attendee.name,
                                        id: attendee.id,
                                        slug: attendee.slug,
                                    })}`}
                                >
                                    <UserAvatar user={attendee} />
                                </Link>
                                <div className="flex max-w-full flex-col items-center justify-center gap-2 overflow-hidden text-base">
                                    <span
                                        className={cn(
                                            "w-full truncate",
                                            customization?.labelClassName,
                                        )}
                                    >
                                        {attendee.name}
                                    </span>
                                    <div className="flex basis-1/5">
                                        <SocialLinksComponent
                                            socialLinks={
                                                attendee.socialLinks as SocialLinks
                                            }
                                            className={cn(
                                                customization?.labelClassName,
                                            )}
                                        />
                                    </div>
                                </div>
                                <If condition={attendee.isHost}>
                                    <div
                                        className={cn(
                                            "absolute right-2 top-2 rounded-lg bg-v3-N-400 px-3 py-1 text-sm font-normal",
                                            customization?.buttons?.default,
                                        )}
                                    >
                                        Host
                                    </div>
                                </If>
                                <If
                                    condition={
                                        !attendee.isHost &&
                                        attendee.hasCheckedIn
                                    }
                                >
                                    <div
                                        className={cn(
                                            "absolute right-2 top-2 flex items-center gap-1 rounded-lg bg-v3-N-400/80 px-3 py-1 text-sm font-normal text-v3-primary",
                                            customization?.buttons?.default,
                                        )}
                                    >
                                        Checkin <LuCheckCircle />
                                    </div>
                                </If>

                                <If condition={!attendee.hasCheckedIn}>
                                    <div className="absolute right-2 top-2 rounded-lg bg-v3-N-400/80 px-3 py-1 text-sm font-normal">
                                        <TooltipProvider delayDuration={0}>
                                            <Tooltip>
                                                <TooltipTrigger className="flex items-center gap-2 text-white/50">
                                                    No Check-In
                                                    <FaInfoCircle />
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    sideOffset={-100}
                                                >
                                                    <p>
                                                        Warning! We can not
                                                        guarantee if that's the
                                                        person you've met since
                                                        the individual never
                                                        checked in at the event
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </If>
                            </CardTitle>
                            <CardDescription
                                className={cn(
                                    "line-clamp-6 break-words pt-2 text-center",
                                    customization?.descriptionClassName,
                                )}
                            >
                                {attendee.bio}
                            </CardDescription>
                        </CardHeader>
                        <CardFooter className="flex grow flex-col items-center justify-center gap-2">
                            <div className="grow"></div>
                            {session?.user?.id !== attendee.id && (
                                <FollowUserButton
                                    userId={attendee.id}
                                    isFollowing={
                                        followingMap.get(attendee.id) !==
                                        undefined
                                    }
                                    refetch={refetch}
                                    isLoading={isLoading}
                                    className="w-full"
                                />
                            )}
                            <ButtonLink
                                href={`/profile/${slugifyUser({
                                    name: attendee.name,
                                    id: attendee.id,
                                    slug: attendee.slug,
                                })}`}
                                className={cn(customization?.buttons?.default)}
                            >
                                View profile
                            </ButtonLink>
                        </CardFooter>
                    </Card>
                ))}
            </div>
        </div>
    );
};
